import { Checkbox, Input } from "antd";
import { isEmpty, isNil } from "lodash";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiSearch } from "react-icons/bi";
import { NAME_SPACES } from "shared/locales/constants";
import { SectionNote } from "./settle-list";
import { ProposalLine, ZoneRow } from "./zone-row";
import { GROUP_KINDS } from "./zone-sections";
import {
  isPendingStatus,
  SUMMARY_KINDS,
  summaryText,
  ZONE_STATUSES,
  zoneLabel,
  zoneName,
  zoneSummary,
  zoneValueText,
} from "./zones";
import { searchable } from "utils/helpers/text";

const VALUE_TONES = { MUTED: "muted", EMPTY: "empty" };

const rowValue = ({ zone, status, summary, mappings, payload, t }) => {
  if (status === ZONE_STATUSES.EMPTY)
    return { value: t("FIELD_PANEL.RESULT_EMPTY"), tone: VALUE_TONES.EMPTY };
  if (summary.kind === SUMMARY_KINDS.EMPTY)
    return { value: t("ZONES.LEFT_EMPTY"), tone: VALUE_TONES.MUTED };
  return { value: zoneValueText({ zone, mappings, payload, t }), tone: null };
};

const AllGroupHead = ({ group, done, count, open, onToggle }) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  return (
    <button
      type="button"
      className="dynamic-documents-editor__group-head dynamic-documents-editor__group-head--toggle"
      data-group-head
      aria-expanded={open}
      onClick={onToggle}
    >
      <span
        className="dynamic-documents-editor__group-title"
        title={group.title}
      >
        {group.title}
      </span>
      <span className="dynamic-documents-editor__group-meta">
        {group.kind === GROUP_KINDS.HEADING
          ? t("ZONES.GROUP_READY", { page: group.page + 1, done, count })
          : t("ZONES.GROUP_READY_COUNT", { done, count })}
      </span>
      <span className="dynamic-documents-editor__group-chevron" aria-hidden>
        {open ? "▾" : "▸"}
      </span>
    </button>
  );
};

const AllFields = ({
  zones,
  groups,
  mode,
  placeOf,
  mappings,
  proposals,
  payload,
  samplePrefix,
  searching,
  pointerZoneId,
  statusOf,
  titleOf,
  allSettled,
  unsettledCount,
  openZoneId,
  focus,
  renderPanel,
  onToggle,
  onFill,
  onOpen,
  onLeaveEmpty,
  onHover,
  onFocus,
  onBlur,
  onBack,
}) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const ZONES = t("ZONES", { returnObjects: true });
  const [search, setSearch] = useState("");
  const [onlyEmpty, setOnlyEmpty] = useState(false);
  const [opened, setOpened] = useState({});
  const [seenFocus, setSeenFocus] = useState(focus);
  const [seenOpen, setSeenOpen] = useState(null);

  const zoneById = useMemo(
    () => new Map(zones.map((zone) => [zone.id, zone])),
    [zones],
  );

  const sections = useMemo(
    () =>
      groups.map((group) => ({
        group,
        search: searchable(group.title),
        rows: group.zoneIds
          .filter((id) => zoneById.has(id))
          .map((id) => {
            const zone = zoneById.get(id);
            const label = titleOf(zone);
            const summary = zoneSummary(zone, mappings);
            const summaryLine = summaryText(summary, t);
            const status = statusOf(zone);
            return {
              zone,
              label,
              status,
              summary: summaryLine,
              ...rowValue({ zone, status, summary, mappings, payload, t }),
              text: searchable(
                `${label} ${zoneLabel(zone, t)} ${zoneName(zone)} ${summaryLine}`,
              ),
            };
          }),
      })),
    [groups, zoneById, mappings, payload, statusOf, titleOf, t],
  );

  const groupOf = (zoneId) =>
    sections.find(({ rows }) => rows.some((row) => row.zone.id === zoneId))
      ?.group.key;

  const keepOpen = (zoneId) => {
    const key = groupOf(zoneId);
    if (!isNil(key)) setOpened((current) => ({ ...current, [key]: true }));
  };

  if (focus !== seenFocus) {
    setSeenFocus(focus);
    if (!isNil(focus)) keepOpen(focus.zoneId);
  }

  if (openZoneId !== seenOpen) {
    setSeenOpen(openZoneId);
    if (!isNil(openZoneId)) keepOpen(openZoneId);
  }

  const query = searchable(search);
  const filtering = !isEmpty(query) || onlyEmpty;
  const emptyCount = sections
    .flatMap(({ rows }) => rows)
    .filter((row) => row.status === ZONE_STATUSES.EMPTY).length;
  const openGroup = groupOf(openZoneId);

  const shown = sections
    .map(({ group, search: title, rows }) => {
      const matching = rows.filter(
        (row) =>
          row.zone.id === openZoneId ||
          ((title.includes(query) || row.text.includes(query)) &&
            (!onlyEmpty || row.status === ZONE_STATUSES.EMPTY)),
      );
      const open =
        group.kind === GROUP_KINDS.FLAT ||
        (opened[group.key] ??
          (group.key === openGroup || (filtering && !isEmpty(matching))));
      return {
        group,
        count: rows.length,
        done: rows.filter((row) => !isPendingStatus(row.status)).length,
        rows: matching,
        open,
      };
    })
    .filter(({ count, rows }) => count > 0 && (!filtering || !isEmpty(rows)));

  const renderRow = ({ zone, label, summary, status, value, tone }) => {
    const rowOpen = zone.id === openZoneId;
    const pending = isPendingStatus(status);
    return (
      <ZoneRow
        key={zone.id}
        zone={zone}
        title={label}
        status={status}
        place={placeOf.get(zone.id)}
        showValue={!pending}
        value={value}
        valueTone={tone}
        valueTitle={summary}
        open={rowOpen}
        pointed={zone.id === pointerZoneId}
        line={
          pending && (
            <ProposalLine
              zone={zone}
              proposals={proposals}
              payload={payload}
              samplePrefix={samplePrefix}
              searching={searching}
              onFill={onFill}
              onOpen={onOpen}
              onLeaveEmpty={onLeaveEmpty}
            />
          )
        }
        panel={rowOpen ? renderPanel(zone) : null}
        onToggle={onToggle}
        onHover={onHover}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    );
  };

  return (
    <div className="dynamic-documents-editor__zones">
      {allSettled && (
        <div
          className="dynamic-documents-editor__all-settled"
          data-all-settled
          tabIndex={-1}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="10" fill="#1f7a45" />
            <path
              d="M7.5 12.5l3 3 6-6.5"
              fill="none"
              stroke="#ffffff"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {ZONES.ALL_SETTLED}
        </div>
      )}
      <div className="dynamic-documents-editor__all-head">
        {unsettledCount > 0 && (
          <button
            type="button"
            className="dynamic-documents-editor__link"
            onClick={onBack}
          >
            {t("ZONES.BACK_TO_SETTLE", { count: unsettledCount })}
          </button>
        )}
        <h2 className="dynamic-documents-editor__zones-title">
          {t("ZONES.ALL_TITLE", { count: zones.length })}
        </h2>
        <Input
          allowClear
          prefix={<BiSearch />}
          placeholder={ZONES.SEARCH}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Checkbox
          className="dynamic-documents-editor__only-empty"
          checked={onlyEmpty}
          onChange={(event) => setOnlyEmpty(event.target.checked)}
        >
          {t("ZONES.ONLY_EMPTY", { count: emptyCount })}
        </Checkbox>
      </div>
      <SectionNote mode={mode} />
      {isEmpty(shown) && (
        <p className="dynamic-documents-editor__muted dynamic-documents-editor__zones-empty">
          {isEmpty(zones) ? ZONES.NO_ZONE : ZONES.NO_MATCH}
        </p>
      )}
      {shown.map(({ group, count, done, rows, open }) => (
        <section
          key={group.key}
          className="dynamic-documents-editor__group"
          data-group={group.key}
        >
          {group.kind !== GROUP_KINDS.FLAT && (
            <AllGroupHead
              group={group}
              done={done}
              count={count}
              open={open}
              onToggle={() =>
                setOpened((current) => ({ ...current, [group.key]: !open }))
              }
            />
          )}
          {open && (
            <ul className="dynamic-documents-editor__rows">
              {rows.map(renderRow)}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
};

export default AllFields;
